/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs-plugin-utc";
import _ from "lodash";

import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";

import { Pagination } from "../../components/Pagination";
import { SessionStatus, TableConfig } from "../../config/Config";
import { apiRequest } from "../../utils/api";
import { useNotification } from "../../context/notification";

import styles from "./Logs.module.scss";
import { PerPage } from "../../components/PerPage";
import { useTranslation } from "react-i18next";

const defaultTableHeader = [
  { name: "completion_datetime", label: "Completion date", width: "20%" },
  { name: "session_id", label: "Session ID", width: "20%" },
  { name: "duration", label: "Duration", width: "10%" },
  { name: "type", label: "Type", width: "10%" },
  { name: "subject", label: "Subjects", width: "30%" },
  { name: "coachee_name", label: "Name", width: "15%" },
  { name: "status", label: "Status", width: "15%" },
];

dayjs.extend(utc);
export function LogsTable({
  filters,
  uid,
  getDataFrom,
  coacheeId,
  coachId,
  searchKey,
  campaignId,
  coachName,
  tableHeader = defaultTableHeader,
  customParam = false,
  isSupportTable = false,
  ...props
}) {
  const { t } = useTranslation();
  const history = useHistory();
  const { addNotification } = useNotification();
  const [logData, setLogData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [offset, setOffset] = useState(1);
  const [totalData, setTotalData] = useState(0);
  const [perPage, setPerPage] = useState(TableConfig.resultPerPage);
  const [columnName, setColumnName] = useState("session_datetime");
  const [sortOrder, setSortOrder] = useState("desc");
  const latestRequestRef = useRef(0);

  async function fetchData() {
    const requestId = ++latestRequestRef.current;
    setIsLoading(true);
    const campaignParam = campaignId ? `&campaign_id=${campaignId}` : "";
    const coachIdParam =
      coachId !== "All Coaches" ? `&coach_id=${coachId}` : "";
    let param = `?uid=${uid}${coacheeId ? "&coachee_id=" + coacheeId : ""
      }&page=${offset}&limit=${perPage}&columnName=${columnName}&order=${sortOrder}${coachIdParam}${campaignParam}&search=${searchKey ? searchKey : ""
      }`;
    if (customParam) {
      param = `${customParam}&page=${offset}&limit=${perPage}&columnName=${columnName}&order=${sortOrder}&search=${searchKey ? searchKey : ""
        }`;
    }
    const response = await apiRequest.get(`${getDataFrom}${param}`);

    if (requestId !== latestRequestRef.current) return;

    if (response.status === 200) {
      const data = response.data;

      const logList = data.data;
      setLogData(logList);
      setTotalData(data.totalData);
    } else {
      addNotification(
        t("error.couldNotGetSession") + ` (Status code: ${response.status})`,
        "error"
      );
    }
    setIsLoading(false);
  }

  useEffect(() => {
    setOffset(1);
  }, [searchKey, customParam]);

  useEffect(() => {
    fetchData();
  }, [
    offset,
    columnName,
    sortOrder,
    uid,
    getDataFrom,
    searchKey,
    campaignId,
    coachId,
    customParam,
    filters,
    perPage,
  ]);

  const handlePaginationChange = offset => {
    setOffset(offset.selected + 1);
  };

  const sortData = name => {
    if (name !== "status") {
      setColumnName(name);
      const order = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(order);
    }
  };

  return (
    <>
      <TableContainer component={Paper} elevation={0}>
        <Table aria-label="Team" className={styles.table}>
          <TableHead className={styles.tableHead}>
            <TableRow>
              {tableHeader &&
                tableHeader.map((tableHead, index) => (
                  <TableCell
                    key={index}
                    style={{ width: tableHead.width }}
                    className={styles.tableHeadCell}
                    onClick={() => {
                      sortData(tableHead.name);
                    }}
                  >
                    {tableHead.label}

                    {columnName === tableHead.name && sortOrder === "asc" && (
                      <span>
                        <KeyboardArrowUpIcon fontSize="small" />
                      </span>
                    )}
                    {columnName === tableHead.name && sortOrder === "desc" && (
                      <span>
                        <KeyboardArrowDownIcon fontSize="small" />
                      </span>
                    )}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {logData &&
              logData.map((row, index) => {
                const isSessionPassed = row.overall_status === "Passed";
                const isSessionIncomplete =
                  row.status === SessionStatus.INCOMPLETE;

                let statusStyle, statusText;

                if (isSessionIncomplete) {
                  statusStyle = styles.statusExpired;
                  statusText = "Expired";
                } else {
                  statusStyle = isSessionPassed
                    ? styles.statusPassed
                    : styles.statusFailed;
                  statusText = row.overall_status || "-";
                }

                return (
                  <TableRow
                    key={index}
                    className={styles.tableRow}
                    onClick={() => {
                      sessionStorage.setItem("logsSearchKey", searchKey);
                      if (props.filterObj) {
                        sessionStorage.setItem(
                          "campaignLogsFilter",
                          JSON.stringify(props.filterObj)
                        );
                      }
                      history.push(`/logs/archive-session/${row.session_id}`);
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <span>
                        {dayjs(row.session_datetime)
                          .add(dayjs().utcOffset(), "minutes")
                          .format("DD MMM YYYY")}
                      </span>
                    </TableCell>
                    <TableCell>{row.session_id}</TableCell>
                    <TableCell>{row.duration} mins</TableCell>
                    {!isSupportTable && <TableCell>{row.type}</TableCell>}
                    <TableCell>
                      <>
                        {row.subject &&
                          typeof row.subject === "string" &&
                          row.subject}
                        {row.subject && typeof row.subject === "object" && (
                          <>
                            {row.subject.map((subj, index) => (
                              <span key={index}>
                                {" "}
                                {subj.subject} - {subj.sub_subject}
                              </span>
                            ))}
                          </>
                        )}
                      </>
                    </TableCell>
                    {coachName && (
                      <>
                        <TableCell> {row.coach_name} </TableCell>
                        <TableCell> {row.coach_employee_id} </TableCell>
                      </>
                    )}

                    <TableCell> {row.coachee_name} </TableCell>
                    <TableCell> {row.coachee_employee_id} </TableCell>
                    <TableCell component="th" scope="row">
                      {row.completion_datetime &&
                        row.completion_datetime !== null &&
                        dayjs && (
                          <span>
                            {dayjs(row.completion_datetime)
                              .add(dayjs().utcOffset(), "minutes")
                              .format("DD MMM YYYY")}
                          </span>
                        )}
                    </TableCell>
                    <TableCell>
                      {row.framework === 1 && (
                        <span className={statusStyle}>{statusText}</span>
                      )}
                      {row.framework === 2 && <span>-</span>}
                    </TableCell>
                  </TableRow>
                );
              })}

            {_.isEmpty(logData) && (
              <TableRow style={{ borderBottom: "1px solid #eeeeee" }}>
                <TableCell style={{ borderBottom: 0 }} colSpan={6}>
                  No team logs found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalData > perPage && (
        <div className={styles.footer}>
          <PerPage limit={perPage} setLimit={setPerPage} />
          <Pagination
            totalData={totalData}
            limit={perPage}
            offset={offset}
            handlePageClick={offset => {
              handlePaginationChange(offset);
            }}
          />
        </div>
      )}
    </>
  );
}